/**
 * Step 5 of the HRT / TRT Plan tab: once the pathway is saved, the ticked products become a treatment plan the
 * same way every MCOS plan does. Nothing here is a second sign flow: it calls the treatment builder's own endpoints
 * (GET /regimens/preview for the patient message, POST /signoff/preview for the live checks, POST /regimens/release
 * to save or send) and opens the shared Review and sign drawer (SignDrawer.tsx), where every check runs again on
 * the server (SG-003) and a controlled item still needs EPCS before it can be signed (RP-11).
 *
 * A plan holds therapy library treatments. A hormone product reaches it through its formulary row's treatment
 * link (`OprItem.treatmentId`); until MCOS links a product, this step says so and sends nothing.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pricingBanner } from '@mcos/types';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { IconNote, IconPencil, IconSend } from './icons';
import { InfoHint, LoadingRows } from './ui';
import { bannerLines, type OprItem, type useOprPrices } from './OprPricing';
import { SignBanner, SignDrawer, type SignoffView } from './SignDrawer';
import { PlanStatus } from './PlanStatus';
import { SaveNote, StepCard } from './HrtUi';
import { hrtKey, joinNames, type HrtPathway, type HrtPatientView } from '../lib/hrt';
import type { HrtPick } from './HrtPlanTab';

type Prices = ReturnType<typeof useOprPrices>;

interface PlanPreview {
  message: string;
  goalHasNumericTarget: boolean;
}

export function HrtSendStep({
  view,
  pathway,
  pick,
  prices,
  onOpenOpr,
  onPlanSent,
}: {
  view: HrtPatientView;
  pathway: HrtPathway;
  pick: HrtPick;
  prices: Prices;
  onOpenOpr: () => void;
  onPlanSent?: (outcome: { tone: 'ok' | 'warn'; text: string }) => void;
}) {
  const queryClient = useQueryClient();
  const provider = useAuth().can('ADMIN', 'CLINICIAN');
  const patientId = view.patient.id;
  const products = (view.products[pathway.id] ?? []).filter((p) => pick.products.includes(p.medicationId));
  const items = products.map((p) => (p.formularyItemId ? prices.byItemId.get(p.formularyItemId) ?? null : null));
  const linked = products.length > 0 && items.every((item): item is OprItem => Boolean(item?.treatmentId));
  const unlinked = products.filter((_, index) => !items[index]?.treatmentId);
  const held = unlinked.filter((p) => p.heldBack);
  const linkedItems = linked ? (items as OprItem[]) : [];
  const treatmentIds = linkedItems.map((item) => item.treatmentId as string);
  const selectionKey = [...treatmentIds].sort().join(',');
  const allPriced = linked && pricingBanner(bannerLines(linkedItems)).allSet;

  // The saved dose goes on the plan line of the medicine it was recorded with.
  const latest = view.selections[0];
  const doseFor = (medicationId: string): string | null =>
    latest?.kind === 'selected' && latest.medicationId === medicationId && latest.doseMgWeek != null ? `${latest.doseMgWeek} mg/week` : null;
  const lines = products.map((p, index) => ({ treatmentId: linkedItems[index]?.treatmentId as string, dose: doseFor(p.medicationId) }));

  const preview = useQuery({
    queryKey: ['treatment-preview', patientId, selectionKey],
    queryFn: () => api.get<PlanPreview>(`/regimens/preview${qs({ patientId, treatments: selectionKey })}`),
    enabled: provider && linked,
  });
  const signoff = useQuery({
    queryKey: ['signoff-preview', patientId, selectionKey],
    queryFn: () => api.post<SignoffView>('/signoff/preview', { patientId, treatments: lines }),
    enabled: provider && linked && allPriced,
  });

  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [signingPlanId, setSigningPlanId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ tone: 'ok' | 'warn' | 'error'; text: string } | null>(null);
  useEffect(() => {
    setMessage(null);
    setEditing(false);
  }, [selectionKey]);

  const release = useMutation({
    mutationFn: (send: boolean) =>
      api.post<{ planId: string }>('/regimens/release', { patientId, treatments: lines, message: message ?? null, send }),
    onSuccess: (result, send) => {
      for (const key of [['treatment-queue'], ['workspace', patientId], ['patient-plans', patientId], ['opr-items'], ['nav-counts'], ['notifications']]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      void queryClient.invalidateQueries({ queryKey: hrtKey(patientId) });
      if (!send) {
        setOutcome({ tone: 'ok', text: 'Saved as a draft. Nothing was sent to the patient.' });
        return;
      }
      // Nothing reaches the patient until the plan is signed (SG-001): the drawer takes it from here.
      setOutcome(null);
      setSigningPlanId(result.planId);
    },
    onError: (caught) => setOutcome({ tone: 'error', text: caught instanceof Error ? caught.message : 'Could not save the plan' }),
  });

  const body = message ?? preview.data?.message ?? '';

  return (
    <StepCard
      step={5}
      title="Send the Plan"
      subtitle={`${pathway.name}, with the products you ticked. Signed and sent the same way as every MCOS treatment plan.`}
      testId="hrt-send-step"
    >
      <PlanStatus patientId={patientId} />
      {!provider ? (
        <p className="rounded-lg bg-paper-deep px-4 py-3 text-[13.5px] text-ink-muted">Only a provider builds, signs and sends a treatment plan.</p>
      ) : products.length === 0 ? (
        <p className="rounded-lg border border-dashed border-hairline px-4 py-4 text-[13.5px] text-ink-muted">Tick at least one product in step 1 to build the plan.</p>
      ) : !linked ? (
        <div className="rounded-lg border border-dashed border-plum-line bg-plum-soft px-4 py-3.5 text-[13.5px] text-plum-ink" data-testid="hrt-send-unlinked">
          <p className="flex flex-wrap items-center gap-2 font-semibold">
            {held.length > 0
              ? `${joinNames(held.map((p) => p.name))} cannot go on a plan yet`
              : `${joinNames(unlinked.map((p) => p.name))} ${unlinked.length === 1 ? 'is' : 'are'} not in your treatment list yet`}
            <InfoHint label="Why the plan cannot be sent yet">
              {held.length > 0
                ? 'The dose library gives these products no automatic dose until MCOS approves the exact product (formulary or SKU approval). Untick them to send the plan with the other products.'
                : 'A treatment plan is made of treatment list entries, and each product needs its formulary row linked to one by MCOS. Untick it to send the plan with the other products.'}
            </InfoHint>
          </p>
          <p className="mt-1 text-[13px] text-plum">
            The pathway choice and its reason are saved. Prices you set stay in your price book.
          </p>
          <button type="button" onClick={onOpenOpr} className="mt-2 text-[13px] font-semibold text-forest underline underline-offset-2">
            Open OPR Pricing
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[12.5px] text-ink-muted">This message will be sent to {view.patient.firstName}. You can edit it before sending.</p>
              <button
                type="button"
                onClick={() => setEditing((was) => !was)}
                className="inline-flex h-[30px] shrink-0 items-center gap-2 rounded-md border border-hairline bg-white px-3.5 text-[13.5px] text-navy hover:bg-mint"
              >
                <IconPencil size={15} />
                {editing ? 'Done editing' : 'Edit Message'}
              </button>
            </div>
            {editing ? (
              <textarea
                value={body}
                onChange={(event) => setMessage(event.target.value)}
                className="mt-2 min-h-[170px] w-full rounded-lg border border-hairline bg-info-soft px-4 py-3 text-[13px] leading-relaxed text-navy focus:border-forest focus:outline-none"
              />
            ) : (
              <div className="mt-2 space-y-2.5 rounded-lg bg-info-soft px-4 py-3.5 text-[12.5px] leading-relaxed text-navy">
                {preview.isLoading ? (
                  <LoadingRows rows={3} />
                ) : (
                  body
                    .split(/\n{2,}/)
                    .map((paragraph) => paragraph.trim())
                    .filter(Boolean)
                    .map((paragraph, index) => <p key={index} className="whitespace-pre-wrap">{paragraph}</p>)
                )}
              </div>
            )}
          </div>
          <div>
            {allPriced && signoff.data ? (
              <SignBanner
                banner={signoff.data.banner}
                buttonLabel={signoff.data.route.mode === 'send_to_sign' ? signoff.data.route.buttonLabel : 'Review and sign'}
                onOpen={() => release.mutate(true)}
                busy={release.isPending}
              />
            ) : (
              <button type="button" disabled className="flex h-[42px] w-full items-center justify-center gap-3 rounded-md bg-forest text-[15px] font-medium text-white disabled:opacity-50">
                <IconSend size={19} />
                {allPriced ? 'Checking the plan' : 'Set every price to send'}
              </button>
            )}
            <button
              type="button"
              disabled={release.isPending}
              onClick={() => release.mutate(false)}
              className="mt-2.5 flex h-[34px] w-full items-center justify-center gap-3 rounded-md border border-hairline bg-white text-[14.5px] text-navy hover:bg-mint disabled:opacity-50"
            >
              <IconNote size={18} />
              Save as Draft
            </button>
          </div>
        </div>
      )}
      {outcome && <SaveNote tone={outcome.tone}>{outcome.text}</SaveNote>}
      {signingPlanId && (
        <SignDrawer
          planId={signingPlanId}
          patientId={patientId}
          onClose={() => setSigningPlanId(null)}
          onSent={(text, tone) => {
            setOutcome({ tone, text });
            onPlanSent?.({ tone, text });
          }}
        />
      )}
    </StepCard>
  );
}
